// ═══════════════════════════════════════════════════════════════════════════════
// Membership pricing catalog — Admin setting + marketing value helpers
// ═══════════════════════════════════════════════════════════════════════════════
import { TASK_KIND } from '../constants/index.js';
import { MEMBERSHIP_PRICING_ITEM_KEYS, MEMBERSHIP_PRICING_LOCALES, MEMBERSHIP_PRICING_LOCKED_KEYS, } from '../types/membershipPricing.js';
import { SUBSCRIPTION_BILLING_INTERVAL, collectLimitedTimePromoCandidates, pickMaxSubscriptionPromo, stripeMinorUnitMultiplier, } from './subscriptionPromotions.js';
export const MEMBERSHIP_PRICING_CATALOG_SETTING_KEY = 'membership_pricing_catalog';
const emptyLocaleText = () => ({
    en: '',
    'zh-Hant': '',
    'zh-Hans': '',
});
const localeText = (en, zhHant, zhHans) => ({
    en,
    'zh-Hant': zhHant,
    'zh-Hans': zhHans,
});
const item = (key, sortOrder, enabled, label, remark = emptyLocaleText()) => ({
    key,
    sortOrder,
    enabled,
    label,
    remark,
});
export const DEFAULT_MEMBERSHIP_PRICING_CATALOG = {
    items: [
        item('accounts', 0, true, localeText('Accounts', '帳號數量', '账号数量'), localeText('Each household pairing includes 1 employer account and 1 Helper account.', '每戶配對包含 1 個僱主帳號及 1 個家務助理帳號。', '每户配对包含 1 个雇主账号及 1 个家务助理账号。')),
        item('pricing', 1, true, localeText('Price', '收費', '收费'), localeText('Trial length follows Admin trial settings. Premium monthly price is from Stripe; promos may apply.', '試用日數依 Admin 試用設定。高級會員月費來自 Stripe；或有優惠適用。', '试用日数依 Admin 试用设定。高级会员月费来自 Stripe；或有优惠适用。')),
        item('task_kinds', 2, true, localeText('Task types', '任務類型', '任务类型'), localeText('Which task kinds the employer may create at this membership level.', '此會員等級可建立的任務類型。', '此会员等级可建立的任务类型。')),
        item('max_total_tasks', 3, true, localeText('Total tasks', '總任務建立數', '总任务建立数'), localeText('Active task slots per pairing (archived tasks do not count). Pickup/medication groups count as 1.', '每個配對的有效任務槽位（已封存不計）。接送／食藥群組計為 1。', '每个配对的有效任务槽位（已封存不计）。接送／食药群组计为 1。')),
        item('max_daily_task_submits', 4, true, localeText('Helper task submits / day', '每日家務助理提交任務', '每日家务助理提交任务'), emptyLocaleText()),
        item('max_daily_task_reviews', 5, true, localeText('Employer reviews / day', '每日僱主審核任務', '每日雇主审核任务'), emptyLocaleText()),
        item('max_daily_receipts', 6, true, localeText('Receipts / day', '每日提交收據', '每日提交收据'), emptyLocaleText()),
        item('max_daily_ai_analyze', 7, true, localeText('AI analyses / day', '每日 AI 分析', '每日 AI 分析'), localeText('Shared daily pool for receipt and inventory AI analysis (Helper).', '收據與盤點 AI 分析共用每日額度（家務助理）。', '收据与盘点 AI 分析共用每日额度（家务助理）。')),
        item('max_comments_per_target', 8, true, localeText('Comments per item', '留言則數', '留言则数'), localeText('Max comments per task instance or receipt.', '每個任務實例或收據的留言上限。', '每个任务实例或收据的留言上限。')),
        item('max_comment_length', 9, true, localeText('Comment length', '留言字數', '留言字数'), emptyLocaleText()),
        item('pin_max_items', 10, true, localeText('Pin messages', '置頂訊息則數', '置顶消息则数'), localeText('Total pin messages per pairing (includes inactive).', '每個配對的置頂訊息總數（含停用）。', '每个配对的置顶消息总数（含停用）。')),
        item('pin_title_max_length', 11, true, localeText('Pin title length', '置頂訊息標題字數', '置顶消息标题字数'), emptyLocaleText()),
        item('pin_content_max_length', 12, true, localeText('Pin content length', '置頂訊息內文字數', '置顶消息内文字数'), emptyLocaleText()),
        item('max_inventory_locations', 13, true, localeText('Inventory locations', '盤點地點上限', '盘点地点上限'), localeText('Max locations per inventory wizard. Not applicable when inventory tasks are unavailable.', '每個盤點 wizard 的地點上限。無盤點任務時不適用。', '每个盘点 wizard 的地点上限。无盘点任务时不适用。')),
        item('max_inventory_categories', 14, true, localeText('Inventory categories', '盤點分類上限', '盘点分类上限'), localeText('Max categories per inventory wizard. Not applicable when inventory tasks are unavailable.', '每個盤點 wizard 的分類上限。無盤點任務時不適用。', '每个盘点 wizard 的分类上限。无盘点任务时不适用。')),
        item('auto_translation', 15, true, localeText('Auto translation', '自動翻譯', '自动翻译'), localeText('Follows the employer membership level.', '跟僱主會員等級。', '跟雇主会员等级。')),
        item('storage_retention', 16, true, localeText('Photo / receipt retention (days)', '相片／收據保留天數', '相片／收据保留天数'), localeText('Retention days for photos and receipts by employer level (Admin-configurable).', '相片／收據保留天數依僱主等級（Admin 可調）。', '相片／收据保留天数依雇主等级（Admin 可调）。')),
        item('receipt_budget', 17, true, localeText('Receipts / budget / settlement', '收據／預算／結算', '收据／预算／结算'), localeText('Available at all levels; daily receipt limits still apply.', '各等級皆可用；仍受每日收據上限限制。', '各等级皆可用；仍受每日收据上限限制。')),
    ],
};
export const isMembershipPricingLocale = (value) => MEMBERSHIP_PRICING_LOCALES.includes(value);
export const isMembershipPricingItemKey = (value) => MEMBERSHIP_PRICING_ITEM_KEYS.includes(value);
export const isMembershipPricingLockedKey = (key) => MEMBERSHIP_PRICING_LOCKED_KEYS.includes(key);
const normalizeLocaleText = (raw, fallback) => {
    const row = raw && typeof raw === 'object' && !Array.isArray(raw)
        ? raw
        : {};
    return {
        en: typeof row.en === 'string' ? row.en : fallback.en,
        'zh-Hant': typeof row['zh-Hant'] === 'string' ? row['zh-Hant'] : fallback['zh-Hant'],
        'zh-Hans': typeof row['zh-Hans'] === 'string' ? row['zh-Hans'] : fallback['zh-Hans'],
    };
};
const defaultItemByKey = (key) => {
    const found = DEFAULT_MEMBERSHIP_PRICING_CATALOG.items.find((row) => row.key === key);
    if (!found) {
        throw new Error(`Missing default membership pricing catalog item: ${key}`);
    }
    return {
        ...found,
        label: { ...found.label },
        remark: { ...found.remark },
    };
};
/** Merge DB/Admin patch with defaults; lock accounts/pricing; stable sort. */
export const normalizeMembershipPricingCatalogSetting = (raw) => {
    const itemsRaw = raw && typeof raw === 'object' && !Array.isArray(raw) && Array.isArray(raw.items)
        ? raw.items
        : [];
    const byKey = new Map();
    for (const entry of itemsRaw) {
        if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
            continue;
        }
        const row = entry;
        if (typeof row.key !== 'string' || !isMembershipPricingItemKey(row.key)) {
            continue;
        }
        const fallback = defaultItemByKey(row.key);
        const locked = isMembershipPricingLockedKey(row.key);
        const sortOrderRaw = Number(row.sortOrder);
        byKey.set(row.key, {
            key: row.key,
            enabled: locked ? true : row.enabled !== false,
            sortOrder: Number.isFinite(sortOrderRaw) ? Math.floor(sortOrderRaw) : fallback.sortOrder,
            label: normalizeLocaleText(row.label, fallback.label),
            remark: normalizeLocaleText(row.remark, fallback.remark),
        });
    }
    const merged = MEMBERSHIP_PRICING_ITEM_KEYS.map((key) => {
        const existing = byKey.get(key);
        if (!existing) {
            return defaultItemByKey(key);
        }
        if (key === 'accounts') {
            return { ...existing, enabled: true, sortOrder: 0 };
        }
        if (key === 'pricing') {
            return { ...existing, enabled: true, sortOrder: 1 };
        }
        return existing;
    });
    const locked = merged.filter((row) => isMembershipPricingLockedKey(row.key));
    const unlocked = merged
        .filter((row) => !isMembershipPricingLockedKey(row.key))
        .sort((a, b) => a.sortOrder - b.sortOrder || a.key.localeCompare(b.key))
        .map((row, index) => ({ ...row, sortOrder: index + 2 }));
    return { items: [...locked, ...unlocked] };
};
export const parseMembershipPricingCatalog = (raw) => normalizeMembershipPricingCatalogSetting(raw);
export const pickMembershipPricingLocaleText = (map, locale) => {
    const primary = map[locale]?.trim() ?? '';
    if (primary !== '') {
        return primary;
    }
    const en = map.en?.trim() ?? '';
    if (en !== '') {
        return en;
    }
    return map['zh-Hant']?.trim() || map['zh-Hans']?.trim() || '';
};
export const MEMBERSHIP_PLAN_NAMES = {
    1: localeText('Trial Member', '試用會員', '试用会员'),
    2: localeText('Free Member', '免費會員', '免费会员'),
    3: localeText('Premium Member', '高級會員', '高级会员'),
};
export const membershipPricingFixedText = {
    unlimited: localeText('Unlimited', '無限', '无限'),
    notApplicable: localeText('N/A', '不適用', '不适用'),
    free: localeText('Free', '免費', '免费'),
    accounts: localeText('1 employer + 1 Helper account', '1 個僱主帳號及 1 個家務助理帳號', '1 个雇主账号及 1 个家务助理账号'),
    available: localeText('Available', '可用', '可用'),
};
export const formatTrialPricingText = (locale, trialDays) => {
    const days = Math.max(1, Math.floor(trialDays));
    if (locale === 'zh-Hant') {
        return `免費${days}日試用`;
    }
    if (locale === 'zh-Hans') {
        return `免费${days}日试用`;
    }
    return `Free ${days}-day trial`;
};
const TASK_KIND_LABELS = {
    [TASK_KIND.STANDARD]: localeText('Standard', '一般', '一般'),
    [TASK_KIND.INVENTORY]: localeText('Home Stock', '家裏存貨', '家里存货'),
    [TASK_KIND.PICKUP_DROPOFF]: localeText('Pickup / drop-off', '接送', '接送'),
    [TASK_KIND.MEDICATION_REMINDER]: localeText('Medication', '食藥', '食药'),
};
export const formatTaskKindsForLocale = (kinds, locale) => {
    if (kinds.length === 0) {
        return pickMembershipPricingLocaleText(membershipPricingFixedText.notApplicable, locale);
    }
    return kinds
        .map((kind) => pickMembershipPricingLocaleText(TASK_KIND_LABELS[kind], locale))
        .join(locale === 'en' ? ', ' : '、');
};
export const formatLimitNumberForLocale = (value, locale) => {
    if (value == null) {
        return pickMembershipPricingLocaleText(membershipPricingFixedText.unlimited, locale);
    }
    return String(value);
};
/** Stripe minor → major display amount (floored). */
export const stripeMinorToMajorAmount = (minor, currency) => {
    const mult = stripeMinorUnitMultiplier(currency);
    if (mult <= 1) {
        return Math.max(0, Math.floor(minor));
    }
    return Math.max(0, Math.floor(minor / mult));
};
export const formatEndsAtDateOnly = (endsAt) => {
    const trimmed = endsAt.trim();
    if (trimmed.length >= 10) {
        return trimmed.slice(0, 10);
    }
    return trimmed;
};
/**
 * Marketing-site promo display.
 * Month may show welcome + limited together; year only limited-time (best offer).
 * Charge-time still picks a single max coupon via resolveSubscriptionPromoForCharge.
 */
export const resolveMarketingMembershipPromo = (input) => {
    const { setting, at, listPriceMinor, currency, billingInterval } = input;
    const listMajor = stripeMinorToMajorAmount(listPriceMinor, currency);
    const limitedCandidates = collectLimitedTimePromoCandidates({
        offers: setting.limitedTimeOffers,
        billingInterval,
        at,
        currency,
    });
    const bestLimited = pickMaxSubscriptionPromo(limitedCandidates);
    const limitedOn = bestLimited != null;
    const limitedMajorOff = bestLimited
        ? Math.floor(bestLimited.amountOff / stripeMinorUnitMultiplier(currency))
        : 0;
    const limitedPrice = Math.max(0, listMajor - limitedMajorOff);
    const endsAtDate = bestLimited?.endsAt ? formatEndsAtDateOnly(bestLimited.endsAt) : '';
    if (billingInterval === SUBSCRIPTION_BILLING_INTERVAL.YEAR) {
        if (!limitedOn || !bestLimited) {
            return null;
        }
        return {
            type: 'limited_time',
            listPriceMajor: listMajor,
            promoPriceMajor: limitedPrice,
            endsAtDate,
        };
    }
    const welcomeOn = setting.newUserFirstMonth.enabled && setting.newUserFirstMonth.amountOff > 0;
    if (!welcomeOn && !limitedOn) {
        return null;
    }
    const welcomePrice = Math.max(0, listMajor - Math.floor(setting.newUserFirstMonth.amountOff));
    if (welcomeOn && limitedOn) {
        return {
            type: 'welcome_and_limited',
            firstMonthPriceMajor: welcomePrice,
            thereafterPromoPriceMajor: limitedPrice,
            listPriceMajor: listMajor,
            endsAtDate,
        };
    }
    if (welcomeOn) {
        return {
            type: 'welcome',
            firstMonthPriceMajor: welcomePrice,
            thereafterPriceMajor: listMajor,
        };
    }
    return {
        type: 'limited_time',
        listPriceMajor: listMajor,
        promoPriceMajor: limitedPrice,
        endsAtDate,
    };
};
const levelKey = (level) => String(level);
const inventoryApplicable = (limits) => limits.allowedTaskKinds.includes(TASK_KIND.INVENTORY);
export const resolveMembershipPricingCell = (key, level, sources) => {
    const { locale } = sources;
    const lk = levelKey(level);
    const limits = sources.limitsByLevel[lk];
    const pin = sources.pinMessageLimitsByLevel[lk];
    if (key === 'accounts') {
        return {
            kind: 'text',
            text: pickMembershipPricingLocaleText(membershipPricingFixedText.accounts, locale),
        };
    }
    if (key === 'pricing') {
        if (level === 1) {
            const trialDays = Math.max(1, Math.floor(sources.trialDaysNew));
            return {
                kind: 'pricing',
                mode: 'trial',
                trialDays,
                text: formatTrialPricingText(locale, trialDays),
            };
        }
        if (level === 2) {
            return {
                kind: 'pricing',
                mode: 'free',
                text: pickMembershipPricingLocaleText(membershipPricingFixedText.free, locale),
            };
        }
        const monthlyListPriceMajor = sources.monthlyListPriceMinor == null
            ? null
            : stripeMinorToMajorAmount(sources.monthlyListPriceMinor, sources.currency);
        const yearlyListPriceMajor = sources.yearlyListPriceMinor == null
            ? null
            : stripeMinorToMajorAmount(sources.yearlyListPriceMinor, sources.currency);
        return {
            kind: 'pricing',
            mode: 'paid',
            currency: sources.currency.toUpperCase(),
            listPriceMajor: monthlyListPriceMajor,
            promo: sources.monthlyListPriceMinor == null ? null : sources.monthlyPromo,
            monthly: {
                listPriceMajor: monthlyListPriceMajor,
                promo: sources.monthlyListPriceMinor == null ? null : sources.monthlyPromo,
            },
            yearly: {
                listPriceMajor: yearlyListPriceMajor,
                promo: sources.yearlyListPriceMinor == null ? null : sources.yearlyPromo,
            },
        };
    }
    if (key === 'task_kinds') {
        return { kind: 'text', text: formatTaskKindsForLocale(limits.allowedTaskKinds, locale) };
    }
    if (key === 'max_total_tasks') {
        return { kind: 'text', text: formatLimitNumberForLocale(limits.maxTotalTasks, locale) };
    }
    if (key === 'max_daily_task_submits') {
        return { kind: 'text', text: String(limits.maxDailyTaskSubmits) };
    }
    if (key === 'max_daily_task_reviews') {
        return { kind: 'text', text: String(limits.maxDailyTaskReviews) };
    }
    if (key === 'max_daily_receipts') {
        return { kind: 'text', text: String(limits.maxDailyReceipts) };
    }
    if (key === 'max_daily_ai_analyze') {
        return { kind: 'text', text: String(limits.maxDailyAiAnalyze) };
    }
    if (key === 'max_comments_per_target') {
        return { kind: 'text', text: String(limits.maxCommentsPerTarget) };
    }
    if (key === 'max_comment_length') {
        return { kind: 'text', text: String(limits.maxCommentLength) };
    }
    if (key === 'pin_max_items') {
        return { kind: 'text', text: String(pin.maxItems) };
    }
    if (key === 'pin_title_max_length') {
        return { kind: 'text', text: String(pin.titleMaxLength) };
    }
    if (key === 'pin_content_max_length') {
        return { kind: 'text', text: String(pin.contentMaxLength) };
    }
    if (key === 'max_inventory_locations') {
        if (!inventoryApplicable(limits)) {
            return {
                kind: 'text',
                text: pickMembershipPricingLocaleText(membershipPricingFixedText.notApplicable, locale),
            };
        }
        return { kind: 'text', text: formatLimitNumberForLocale(limits.maxInventoryLocations, locale) };
    }
    if (key === 'max_inventory_categories') {
        if (!inventoryApplicable(limits)) {
            return {
                kind: 'text',
                text: pickMembershipPricingLocaleText(membershipPricingFixedText.notApplicable, locale),
            };
        }
        return { kind: 'text', text: formatLimitNumberForLocale(limits.maxInventoryCategories, locale) };
    }
    if (key === 'auto_translation') {
        return { kind: 'boolean', value: limits.autoTranslationEnabled };
    }
    if (key === 'storage_retention') {
        return { kind: 'text', text: String(sources.photoRetentionDaysByLevel[lk]) };
    }
    if (key === 'receipt_budget') {
        return { kind: 'boolean', value: true };
    }
    return {
        kind: 'text',
        text: pickMembershipPricingLocaleText(membershipPricingFixedText.notApplicable, locale),
    };
};
//# sourceMappingURL=membershipPricingCatalog.js.map